// src/pages/user/ProfilePage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import FormInput from '../../components/resume/FormInput';

const API = process.env.REACT_APP_API_URL;

const ProfilePage = () => {
    const { isPremium, premiumSource, premiumUntil, cancelAtPeriodEnd, refreshEntitlements } = useAuth();

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [message, setMessage] = useState({ type: '', text: '' });
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(true);
    const [portalLoading, setPortalLoading] = useState(false);
    const [subLoading, setSubLoading] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [hasStripeAccount, setHasStripeAccount] = useState(true);

    const fetchProfile = useCallback(async () => {
        setIsFetching(true);
        try {
            const response = await fetch(`${API}/api/get_profile.php`, {
                method: 'GET', credentials: 'include', headers: { 'Accept': 'application/json' },
            });
            const result = await response.json();
            if (response.ok && result.status === 'success') {
                setFirstName(result.first_name || '');
                setLastName(result.last_name || '');
                setPhoneNumber(result.phone_number || '');
                setDateOfBirth(result.date_of_birth || '');
            } else {
                setMessage({ type: 'error', text: result.message || 'Failed to load profile.' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'An error occurred while loading your profile.' });
        } finally {
            setIsFetching(false);
        }
    }, []);

    useEffect(() => { fetchProfile(); }, [fetchProfile]);
    useEffect(() => { if (refreshEntitlements) refreshEntitlements(); }, [refreshEntitlements]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });
        setIsLoading(true);
        try {
            const response = await fetch(`${API}/api/update_profile.php`, {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({
                    first_name: firstName, last_name: lastName,
                    phone_number: phoneNumber, date_of_birth: dateOfBirth || null,
                }),
            });
            const result = await response.json();
            if (response.ok && result.status === 'success') {
                setMessage({ type: 'success', text: 'Profile updated.' });
            } else {
                setMessage({ type: 'error', text: result.message || 'Failed to update profile.' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'An error occurred. Please try again later.' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleManageBilling = async () => {
        setPortalLoading(true);
        setMessage({ type: '', text: '' });
        try {
            const response = await fetch(`${API}/api/create_billing_portal.php`, {
                method: 'POST', credentials: 'include', headers: { 'Accept': 'application/json' },
            });
            const result = await response.json();
            if (response.ok && result.status === 'success' && result.url) {
                window.location = result.url;
            } else {
                setHasStripeAccount(false);
                setMessage({ type: 'error', text: result.message || 'No billing account found.' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Unable to open billing portal. Please try again.' });
        } finally {
            setPortalLoading(false);
        }
    };

    const handleSetCancel = async (cancel) => {
        setSubLoading(true);
        setMessage({ type: '', text: '' });
        try {
            const response = await fetch(`${API}/api/set_subscription_cancel.php`, {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ cancel }),
            });
            const result = await response.json();
            if (response.ok && result.status === 'success') {
                if (refreshEntitlements) await refreshEntitlements();
                setMessage({ type: 'success', text: cancel ? 'Your subscription will end at the period’s close.' : 'Your subscription has been resumed.' });
            } else {
                setMessage({ type: 'error', text: result.message || 'Could not update your subscription.' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Something went wrong. Please try again.' });
        } finally {
            setSubLoading(false);
            setShowCancelModal(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return null;
        const datePart = String(dateStr).split(/[ T]/)[0];
        const d = new Date(datePart + 'T00:00:00Z');
        if (isNaN(d.getTime())) return null;
        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
    };

    if (isFetching) {
        return (
            <div className="flex flex-col items-center justify-center py-32 text-neutral-400">
                <div className="h-8 w-8 rounded-full border-2 border-neutral-700 border-t-cyan-400 animate-spin mb-4" />
                Loading profile…
            </div>
        );
    }

    const isSubscription = premiumSource === 'subscription';
    const isAnnual = premiumSource === 'annual';
    const cardClass = 'rounded-2xl border border-white/10 bg-[#0b0e14]/60 backdrop-blur-sm p-6 sm:p-7';

    return (
        <div className="px-4 py-10 sm:py-14 text-white">
            <div className="max-w-xl mx-auto">
                <header className="mb-8">
                    <h1 className="text-3xl font-bold tracking-tight">My Profile</h1>
                    <p className="text-sm text-neutral-400 mt-1">Manage your account, membership, and details.</p>
                </header>

                {/* Membership card */}
                <section className={`${cardClass} mb-6 relative overflow-hidden`}>
                    {isPremium && (
                        <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-cyan-500/20 blur-3xl" />
                    )}
                    <div className="relative">
                        <div className="flex items-center gap-2 mb-4">
                            <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
                            <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-400">Membership</h2>
                        </div>

                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div>
                                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${isPremium ? 'bg-cyan-500/15 text-cyan-300 ring-1 ring-cyan-500/40' : 'bg-white/5 text-neutral-300 ring-1 ring-white/10'}`}>
                                    {isPremium ? '★ Premium' : 'Free'}
                                </span>
                                <div className="mt-2 text-sm text-neutral-300">
                                    {!isPremium && (
                                        <p className="text-neutral-400">2 AI optimizations/day · download locked</p>
                                    )}
                                    {isPremium && isAnnual && (
                                        <p>Annual pass — Premium until <span className="text-white font-medium">{formatDate(premiumUntil)}</span>. Won't auto-renew.</p>
                                    )}
                                    {isPremium && isSubscription && !cancelAtPeriodEnd && (
                                        <p>Renews on <span className="text-white font-medium">{formatDate(premiumUntil)}</span>.</p>
                                    )}
                                    {isPremium && isSubscription && cancelAtPeriodEnd && (
                                        <p className="text-amber-300">Cancels — Premium until <span className="font-medium">{formatDate(premiumUntil)}</span>.</p>
                                    )}
                                </div>
                            </div>

                            {!isPremium && (
                                <Link to="/upgrade" className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold py-2 px-5 rounded-lg transition-colors shadow-lg shadow-cyan-500/20">
                                    Upgrade
                                </Link>
                            )}
                        </div>

                        {/* Premium actions */}
                        {isPremium && (
                            <div className="mt-5 pt-5 border-t border-white/10 flex flex-wrap gap-2">
                                {hasStripeAccount && (
                                    <button onClick={handleManageBilling} disabled={portalLoading}
                                        className="text-sm font-medium text-neutral-200 bg-white/5 hover:bg-white/10 border border-white/10 py-2 px-4 rounded-lg disabled:opacity-50 transition-colors">
                                        {portalLoading ? 'Opening…' : 'Manage billing'}
                                    </button>
                                )}
                                {isSubscription && !cancelAtPeriodEnd && (
                                    <button onClick={() => setShowCancelModal(true)} disabled={subLoading}
                                        className="text-sm font-medium text-neutral-400 hover:text-red-300 py-2 px-3 rounded-lg disabled:opacity-50 transition-colors">
                                        Cancel subscription
                                    </button>
                                )}
                                {isSubscription && cancelAtPeriodEnd && (
                                    <button onClick={() => handleSetCancel(false)} disabled={subLoading}
                                        className="text-sm font-semibold text-cyan-300 hover:text-cyan-200 bg-cyan-500/10 border border-cyan-500/30 py-2 px-4 rounded-lg disabled:opacity-50 transition-colors">
                                        {subLoading ? 'Working…' : 'Resume subscription'}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </section>

                {/* Personal info card */}
                <section className={cardClass}>
                    <div className="flex items-center gap-2 mb-5">
                        <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
                        <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-400">Personal Information</h2>
                    </div>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <FormInput label="First Name" type="text" name="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="given-name" />
                        <FormInput label="Last Name" type="text" name="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} autoComplete="family-name" />
                        <FormInput label="Phone Number" type="tel" name="phoneNumber" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} autoComplete="tel" />
                        <div className="flex flex-col space-y-1">
                            <label htmlFor="dateOfBirth" className="text-sm font-medium text-neutral-300">Date of Birth</label>
                            <input id="dateOfBirth" type="date" name="dateOfBirth" value={dateOfBirth}
                                onChange={(e) => setDateOfBirth(e.target.value)}
                                max={new Date().toISOString().split('T')[0]} autoComplete="bday"
                                className="bg-black/30 border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/60 focus:border-transparent" />
                            <p className="text-[11px] text-neutral-500">Used for birthday offers — optional.</p>
                        </div>

                        {message.text && (
                            <p className={`text-sm ${message.type === 'error' ? 'text-red-400' : 'text-emerald-400'}`}>{message.text}</p>
                        )}

                        <button type="submit" disabled={isLoading}
                            className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold py-2.5 px-4 rounded-lg disabled:opacity-50 transition-colors shadow-lg shadow-cyan-500/10">
                            {isLoading ? 'Saving…' : 'Save Changes'}
                        </button>
                    </form>
                </section>
            </div>

            {showCancelModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => !subLoading && setShowCancelModal(false)} />
                    <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#0b0e14] p-6 shadow-2xl">
                        <div className="flex items-start gap-3">
                            <div className="h-10 w-10 shrink-0 rounded-full bg-amber-500/15 ring-1 ring-amber-500/40 flex items-center justify-center text-amber-300 font-bold">!</div>
                            <div>
                                <h3 className="text-lg font-semibold text-white">Cancel Premium?</h3>
                                <p className="text-sm text-neutral-400 mt-1.5">
                                    You'll keep Premium until <span className="text-white font-medium">{formatDate(premiumUntil)}</span>, then your plan reverts to Free — PDF download locks and the optimizer drops to 2/day. You can resume anytime before then.
                                </p>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <button onClick={() => setShowCancelModal(false)} disabled={subLoading}
                                className="px-4 py-2 text-sm font-medium text-neutral-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors">
                                Keep Premium
                            </button>
                            <button onClick={() => handleSetCancel(true)} disabled={subLoading}
                                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-500 rounded-lg disabled:opacity-50 transition-colors">
                                {subLoading ? 'Cancelling…' : 'Yes, cancel'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfilePage;
